import { Head, usePage, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { PulseDashboard } from "@/components/PulseDashboard"
import { TrafficExclusions, type Exclusion } from "@/components/analytics/TrafficExclusions"
import type { PageProps, SeoAuditReport } from "@/types/inertia"

type Props = PageProps & {
  brand: "gisela" | "sanctuary" | null
  total_contacts: number
  contact_delta: number
  latest_report: SeoAuditReport | null
  recent_reports: SeoAuditReport[]
  exclusions: Exclusion[]
  current_ip: string
  current_ip_excluded: boolean
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return <span className="text-ink-muted text-sm">no change</span>
  const positive = delta > 0
  return (
    <span className={positive ? "text-sm text-green-600" : "text-sm text-red-500"}>
      {positive ? "↑" : "↓"} {Math.abs(delta)} vs last month
    </span>
  )
}

function ScoreCircle({ score, label }: { score: number; label: string }) {
  const color =
    score >= 90 ? "#7BA696" : score >= 50 ? "#D97706" : "#EF4444"
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-semibold text-white"
        style={{ backgroundColor: color }}
      >
        {score}
      </div>
      <span className="text-center text-xs text-ink-muted">{label}</span>
    </div>
  )
}

export default function AdminAnalytics() {
  const { props } = usePage<Props>()
  const {
    brand,
    total_contacts,
    contact_delta,
    latest_report,
    recent_reports,
    exclusions,
    current_ip,
    current_ip_excluded,
  } = props

  const brandTitle =
    brand === "gisela" ? "Analytics — Gisela Ramirez" : brand === "sanctuary" ? "Analytics — Sanctuary" : "Analytics"

  return (
    <AdminShell>
      <Head title={brandTitle}>
        <meta name="description" content="Site analytics dashboard for Sanctuary Therapy Centre." />
        <meta property="og:title" content="Analytics" />
        <meta property="og:description" content="Site analytics dashboard for Sanctuary Therapy Centre." />
      </Head>
      <PageHeader
        title={brandTitle}
        description="Traffic, visitors, and contact form submissions."
      />

      {/* Brand filter */}
      <div className="mt-4 flex flex-wrap gap-2">
        {[
          { key: null, label: "Both websites", href: "/admin/analytics" },
          { key: "gisela", label: "Gisela Ramirez", href: "/admin/analytics?brand=gisela" },
          { key: "sanctuary", label: "Sanctuary", href: "/admin/analytics?brand=sanctuary" },
        ].map((f) => (
          <Link
            key={f.label}
            href={f.href}
            className="toggle-button no-underline"
            aria-pressed={brand === f.key}
          >
            {f.label}
          </Link>
        ))}
      </div>

      {/* Pulse traffic dashboard (period selector, realtime, stats, charts) */}
      <PulseDashboard brand={brand} />

      {/* Contacts + SEO agent */}
      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-hairline bg-surface p-5">
          <p className="text-sm text-ink-muted">Contact submissions</p>
          <p className="mt-1 text-3xl font-light text-ink-display">
            {total_contacts.toLocaleString()}
          </p>
          <div className="mt-1">
            <DeltaBadge delta={contact_delta} />
          </div>
        </div>

        {/* SEO Agent status */}
        <div className="rounded-lg border border-hairline bg-surface p-5">
          <h2 className="mb-4">SEO Agent</h2>
          {latest_report ? (
            <>
              <div className="flex flex-wrap gap-4 justify-center">
                <ScoreCircle score={latest_report.performance_score} label="Performance" />
                <ScoreCircle score={latest_report.seo_score} label="SEO" />
                <ScoreCircle score={latest_report.accessibility_score} label="Accessibility" />
                <ScoreCircle score={latest_report.best_practices_score} label="Best Practices" />
              </div>
              <p className="mt-3 text-center text-xs text-ink-muted">
                Last run: {new Date(latest_report.created_at).toLocaleDateString()}
              </p>
            </>
          ) : (
            <p className="py-4 text-center text-sm text-ink-muted">
              No audit reports yet.
            </p>
          )}
        </div>

        {/* Recent activity */}
        <div className="rounded-lg border border-hairline bg-surface">
          <div className="border-b border-hairline px-5 py-3">
            <h2>Recent agent activity</h2>
          </div>
          {recent_reports.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-ink-muted">No activity yet.</p>
          ) : (
            <ul className="divide-y divide-hairline">
              {recent_reports.map((r) => (
                <li key={r.id} className="px-5 py-3">
                  <p className="text-xs text-ink-muted">
                    {new Date(r.created_at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                  <p className="mt-0.5 text-sm text-ink-body">
                    <span className="text-green-600">{r.pass_count} passed</span>
                    {" · "}
                    <span className="text-amber-600">{r.warn_count} warnings</span>
                    {" · "}
                    <span className="text-red-500">{r.fail_count} failed</span>
                  </p>
                  {r.summary && (
                    <p className="mt-0.5 text-xs text-ink-muted line-clamp-2">{r.summary}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Keeping the owner's own browsing out of the traffic figures */}
      <div className="mt-8">
        <TrafficExclusions
          exclusions={exclusions}
          currentIp={current_ip}
          currentIpExcluded={current_ip_excluded}
        />
      </div>
    </AdminShell>
  )
}
